import { useState } from "react";
import { Link } from "react-router-dom";
import { Smartphone, Apple, Monitor, Share, PlusSquare, MoreVertical, Download, CheckCircle2, Bell, Zap, WifiOff } from "lucide-react";
import { toast } from "sonner";
import { useInstallPrompt, iosBrowser } from "@/lib/install";
import { usePublicConfig } from "@/lib/site";

const PlayBadge = ({ url }) => (
  <a href={url} target="_blank" rel="noopener noreferrer" data-testid="install-play-link" className="inline-flex items-center gap-2 rounded-xl bg-black border border-slate-700 px-4 py-2.5 text-sm font-medium hover:border-slate-500 transition-colors">
    <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true"><path fill="#34A853" d="M3.6 2.3 13.3 12 3.6 21.7A2 2 0 0 1 3 20.3V3.7a2 2 0 0 1 .6-1.4z"/><path fill="#FBBC04" d="m16.8 8.5 3.4 1.9c1.3.8 1.3 2.4 0 3.2l-3.4 1.9L13.3 12z"/><path fill="#EA4335" d="M3.6 21.7 13.3 12l3.5 3.5-11.7 6.7c-.5.3-1 .3-1.5-.5z"/><path fill="#4285F4" d="M3.6 2.3c.5-.8 1-.8 1.5-.5l11.7 6.7-3.5 3.5z"/></svg>
    Get it on Google Play
  </a>
);
const AppStoreBadge = ({ url }) => (
  <a href={url} target="_blank" rel="noopener noreferrer" data-testid="install-appstore-link" className="inline-flex items-center gap-2 rounded-xl bg-black border border-slate-700 px-4 py-2.5 text-sm font-medium hover:border-slate-500 transition-colors">
    <Apple className="w-5 h-5" /> Download on the App Store
  </a>
);

const Step = ({ n, icon: Icon, children }) => (
  <li className="flex gap-3 items-start">
    <span className="w-6 h-6 rounded-full bg-cyan-500/15 border border-cyan-900/60 text-cyan-200 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{n}</span>
    <span className="text-sm text-slate-300 leading-relaxed flex-1">{children}</span>
    {Icon && <Icon className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />}
  </li>
);

const AndroidSteps = () => (
  <ol className="space-y-2.5" data-testid="install-android-steps">
    <Step n={1} icon={MoreVertical}>Open <strong>ev-idex.com</strong> in <strong>Chrome</strong> and tap the <strong>⋮ menu</strong> (top-right).</Step>
    <Step n={2} icon={Download}>Tap <strong>"Install app"</strong> (or <strong>"Add to Home screen"</strong>).</Step>
    <Step n={3} icon={CheckCircle2}>Tap <strong>Install</strong>. The EV-IDEX icon appears on your home screen and opens full-screen.</Step>
  </ol>
);

const IosSteps = ({ browser }) => (
  <ol className="space-y-2.5" data-testid="install-ios-steps">
    {browser !== "safari" && <Step n={0}>You're in {browser === "chrome" ? "Chrome" : "another browser"} — Apple only allows installing from <strong>Safari</strong>. Open <strong>ev-idex.com</strong> in Safari first.</Step>}
    <Step n={1} icon={Share}>In <strong>Safari</strong>, tap the <strong>Share</strong> button (the square with an arrow at the bottom of the screen).</Step>
    <Step n={2} icon={PlusSquare}>Scroll down and tap <strong>"Add to Home Screen"</strong>.</Step>
    <Step n={3} icon={CheckCircle2}>Tap <strong>Add</strong>. EV-IDEX installs with its logo and opens like a normal app.</Step>
  </ol>
);

const DesktopSteps = () => (
  <ol className="space-y-2.5" data-testid="install-desktop-steps">
    <Step n={1} icon={Download}>In Chrome or Edge, click the <strong>install icon</strong> at the right end of the address bar (a monitor with a down-arrow).</Step>
    <Step n={2} icon={CheckCircle2}>Click <strong>Install</strong>. EV-IDEX opens in its own window and is pinned to your dock / taskbar.</Step>
  </ol>
);

export const InstallButton = ({ className = "", label = "Install EV-IDEX app", source = "hero" }) => {
  const { canPrompt, prompt, standalone } = useInstallPrompt();
  if (standalone) return null;
  const onClick = async () => {
    if (canPrompt) {
      const r = await prompt();
      if (r === "accepted") toast.success("EV-IDEX is installing — look for the icon on your home screen.");
      return;
    }
    const el = document.getElementById("install");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    else window.location.href = "/install";
  };
  return (
    <button type="button" onClick={onClick} data-testid={`install-app-btn-${source}`} className={`inline-flex items-center gap-2 ${className}`}>
      <Download className="w-4 h-4" /> {label}
    </button>
  );
};

export const InstallSection = ({ compact = false }) => {
  const { canPrompt, prompt, platform, standalone } = useInstallPrompt();
  const cfg = usePublicConfig();
  const [tab, setTab] = useState(platform);
  const browser = iosBrowser();

  const nativePrompt = async () => {
    const r = await prompt();
    if (r === "accepted") toast.success("EV-IDEX is installing — look for the icon on your home screen.");
    else if (r === "dismissed") toast.message("No problem — you can install any time from this page.");
  };

  if (standalone) {
    return (
      <div id="install" className="apx-card p-6 flex items-center gap-3" data-testid="install-already">
        <CheckCircle2 className="w-6 h-6 text-emerald-300" />
        <div><div className="font-display font-semibold">You're using the installed EV-IDEX app</div><div className="text-sm text-slate-400">Updates arrive automatically — nothing else to do.</div></div>
      </div>
    );
  }

  const tabs = [["android", "Android", Smartphone], ["ios", "iPhone / iPad", Apple], ["desktop", "Desktop", Monitor]];

  return (
    <section id="install" className={compact ? "" : "py-14 border-t border-slate-800/70"} data-testid="install-section">
      {!compact && (
        <div className="mb-8 max-w-2xl">
          <div className="text-[11px] font-mono-ticker uppercase tracking-widest text-slate-500 mb-2">Download EV-IDEX App</div>
          <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight">Install EV-IDEX on your phone in under a minute</h2>
          <p className="text-slate-400 mt-3 text-sm md:text-base leading-relaxed">EV-IDEX installs straight from this website — no app store account needed. You get the EV-IDEX icon on your home screen, a full-screen app, push alerts and instant updates. It's the same secure account you use on the web.</p>
        </div>
      )}

      <div className="grid lg:grid-cols-[1.1fr_1fr] gap-6">
        <div className="apx-card p-5 md:p-6 space-y-5">
          <div className="flex flex-wrap gap-2" role="tablist" data-testid="install-tabs">
            {tabs.map(([k, label, Icon]) => (
              <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`install-tab-${k}`} className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm border transition-colors ${tab === k ? "border-cyan-700/60 bg-cyan-500/10 text-cyan-100" : "border-slate-700 text-slate-300 hover:border-slate-500"}`}>
                <Icon className="w-4 h-4" /> {label}{k === platform && <span className="text-[10px] text-slate-500 ml-1">(your device)</span>}
              </button>
            ))}
          </div>

          {tab === "android" && (
            <div className="space-y-4">
              {canPrompt ? (
                <button type="button" onClick={nativePrompt} data-testid="install-native-btn" className="btn-primary rounded-xl px-5 py-3 text-base font-semibold inline-flex items-center gap-2 w-full sm:w-auto justify-center"><Download className="w-5 h-5" /> Install EV-IDEX now</button>
              ) : (
                <p className="text-sm text-slate-400">One-tap install appears here when you open this page in Chrome on Android. Otherwise follow these steps:</p>
              )}
              <AndroidSteps />
              {cfg.play_store_url ? <PlayBadge url={cfg.play_store_url} /> : <p className="text-xs text-slate-500" data-testid="install-play-pending">Google Play listing: coming soon. Installing from the website gives you the same app today.</p>}
            </div>
          )}
          {tab === "ios" && (
            <div className="space-y-4">
              <IosSteps browser={platform === "ios" ? browser : "safari"} />
              <p className="text-xs text-slate-500">Push alerts on iPhone require iOS 16.4 or later and work once the app is installed from Safari.</p>
              {cfg.app_store_url ? <AppStoreBadge url={cfg.app_store_url} /> : <p className="text-xs text-slate-500" data-testid="install-appstore-pending">App Store listing: coming soon. Installing from Safari gives you the full app today.</p>}
            </div>
          )}
          {tab === "desktop" && (
            <div className="space-y-4">
              {canPrompt && <button type="button" onClick={nativePrompt} data-testid="install-native-btn" className="btn-primary rounded-xl px-5 py-3 text-base font-semibold inline-flex items-center gap-2"><Download className="w-5 h-5" /> Install EV-IDEX now</button>}
              <DesktopSteps />
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="apx-card p-5 flex items-center gap-4" data-testid="install-icon-preview">
            <img src="/icons/icon-192.png" alt="EV-IDEX app icon" className="w-16 h-16 rounded-2xl shadow-lg" />
            <div><div className="font-display font-semibold">EV-IDEX</div><div className="text-xs text-slate-400">This is the icon you'll see on your home screen.</div></div>
          </div>
          <ul className="apx-card p-5 space-y-3 text-sm text-slate-300">
            <li className="flex gap-2.5"><Zap className="w-4 h-4 text-cyan-300 shrink-0 mt-0.5" /> Opens full-screen like a native app, with live prices streaming by the second</li>
            <li className="flex gap-2.5"><Bell className="w-4 h-4 text-cyan-300 shrink-0 mt-0.5" /> Price, bracket and support/resistance alerts delivered as push notifications</li>
            <li className="flex gap-2.5"><WifiOff className="w-4 h-4 text-cyan-300 shrink-0 mt-0.5" /> Works offline for recently viewed pages; updates install automatically</li>
            <li className="flex gap-2.5"><CheckCircle2 className="w-4 h-4 text-cyan-300 shrink-0 mt-0.5" /> Same account, same simulated portfolio — no real funds, ever</li>
          </ul>
          {!compact && <p className="text-xs text-slate-500">Need help? <Link to="/contact" className="text-cyan-300 hover:underline">Contact & Support</Link> · Full guide: <Link to="/install" className="text-cyan-300 hover:underline" data-testid="install-guide-link">ev-idex.com/install</Link></p>}
        </div>
      </div>
    </section>
  );
};
